<ng-template #content>
  <form class="needs-validation" [formGroup]="form" (ngSubmit)="onSubmit()" [class.submitted]="submitted" novalidate>
    <div class="modal-header">
      <h4 class="modal-title">{{ 'NAV_MY_PROFILE' | agrTranslate }}</h4>
      <button type="button" class="btn-close" (click)="onDismissModal()"></button>
    </div>

    <div class="modal-body">
      <!-- Name -->
      <div id="my-profile-name" class="form-group">
        <label for="my-profile-name-input">{{ 'NAME' | agrTranslate }}</label>
        <input type="text" id="my-profile-name-input" class="form-control" formControlName="name" ngbAutofocus />
        <div class="invalid-feedback">
          @if (name.getError('required')) {
          <div>{{ 'REQUIRED' | agrTranslate }}</div>
          } @if (name.getError('maxlength')) {
          <div>{{ 'INPUT_IS_TOO_LONG' | agrTranslate }}</div>
          }
        </div>
      </div>

      <!-- Language -->
      <div id="my-profile-language" class="form-group">
        <label for="my-profile-language-select">{{ 'LANGUAGE' | agrTranslate }}</label>

        @if (authService.hasFeature('developmentMode')) {
        <span class="agr-mini-tools amt-area pull-right" [ngbTooltip]="'Disable translation overrides'">
          <button
            type="button"
            class="amt-btn"
            [class.active]="disableFallbackTranslations"
            [class.text-danger]="disableFallbackTranslations"
            (click)="toggleFallbackTranslations()">
            <i class="fa fa-wrench"></i>
          </button>
        </span>
        }

        <select id="my-profile-language-select" class="form-control" formControlName="language">
          @for (language of availableLanguages; track trackLanguage($index, language)) {
          <option [ngValue]="language.code">
            {{ language.caption }}
          </option>
          }
        </select>

        <div class="invalid-feedback">
          @if (name.getError('required')) {
          <div>{{ 'REQUIRED' | agrTranslate }}</div>
          } @if (name.getError('maxlength')) {
          <div>{{ 'INPUT_IS_TOO_LONG' | agrTranslate }}</div>
          }
        </div>
      </div>

      <!-- Locale / Date and number formatting -->
      <div id="my-profile-locale" class="form-group">
        <label for="my-profile-locale-select">{{ 'LOCALE' | agrTranslate }}</label>
        <ng-template #localeInfoPopContent>
          <p>{{ 'LOCALE_INFO' | agrTranslate }}</p>
          <p>
            {{ 'DATE_LOCALE' | agrTranslate }}: <strong>{{ formatService.dateLocale }}</strong
            ><br />
            {{ 'NUMBER_LOCALE' | agrTranslate }}: <strong>{{ formatService.numberLocale }}</strong>
          </p>
          <strong>{{ 'DATE' | agrTranslate }}</strong
          >: {{ now | agrFormat: 'date:fullDate' }}<br />
          <strong>{{ 'CURRENCY' | agrTranslate }}</strong
          >: {{ 10015463.51 | agrFormat: 'currency' }}<br />
          <strong>{{ 'NUMBER' | agrTranslate }}</strong
          >: {{ 10500.2 | agrFormat: 'number' }}<br />
          <strong>{{ 'PERCENTAGE' | agrTranslate }}</strong
          >: {{ 0.245 | agrFormat: 'percent' }}
        </ng-template>
        <button type="button" id="locale-info" class="btn btn-help" placement="right" [ngbPopover]="localeInfoPopContent" popoverTitle="">
          <i class="fa fa-question-circle ms-2"></i>
        </button>
        <select id="my-profile-locale-select" class="form-control" formControlName="locale" (change)="onLocaleChange()">
          @for (locale of availableLocales; track locale) {
          <option [ngValue]="locale.code">
            {{ locale.caption }}
          </option>
          }
        </select>
      </div>

      <!-- User info -->
      <dl id="my-profile-user-info" class="agr-dl">
        <div class="row" data-cy="my-profile-username-row">
          <dt class="col-3">{{ 'USERNAME' | agrTranslate }}</dt>
          <dd class="col-9" title="{{ user.username }}">{{ [user.username] }}</dd>
        </div>
        <div class="row" data-cy="my-profile-email-row">
          <dt class="col-3">{{ 'EMAIL' | agrTranslate }}</dt>
          <dd class="col-9" title="{{ user.email }}">{{ user.email }}</dd>
        </div>
        <div class="row" data-cy="my-profile-roles-row">
          <dt class="col-3">{{ 'ROLES' | agrTranslate }}</dt>
          <dd class="col-9" title="{{ userRoles }}">{{ userRoles }}</dd>
        </div>
        <div class="row" data-cy="my-profile-access-groups-row">
          @if (accessGroupsActive) {
          <dt class="col-3">{{ 'ACCESS_GROUPS' | agrTranslate }}</dt>
          } @if (accessGroupsActive) {
          <dd class="col-9" title="{{ userAccessGroups }}">{{ userAccessGroups }}</dd>
          }
        </div>
      </dl>

      <!-- Password Elements -->
      <div class="card bg-light" [ngClass]="{ 'card bg-light': editingPassword }">
        <div class="card-body pb-1 pt-2" [class.card-body]="editingPassword">
          <!-- Buttons -->
          <div id="my-profile-password-buttons" class="form-group">
            <button type="button" class="btn btn-link ps-0" (click)="showChangePassword()" [hidden]="editingPassword">
              {{ 'CHANGE_PASSWORD' | agrTranslate }}
            </button>
            <button type="button" class="btn-close" (click)="hideChangePassword()" [hidden]="!editingPassword"></button>
          </div>

          <div [formGroup]="newPassword" class="form-group" [hidden]="!editingPassword">
            <!-- Current Password -->
            <div id="my-profile-current-password" class="form-group">
              <label for="my-profile-current-password-input">{{ 'CURRENT_PASSWORD' | agrTranslate }}</label>
              <input
                type="password"
                id="my-profile-current-password-input"
                class="form-control"
                formControlName="currentPassword"
                autocomplete="off" />
              <div class="invalid-feedback">
                @if (currentPassword.getError('required')) {
                <div>{{ 'REQUIRED' | agrTranslate }}</div>
                } @if (currentPassword.getError('incorrect')) {
                <div>{{ 'INCORRECT_PASSWORD' | agrTranslate }}</div>
                } @if (currentPassword.getError('minlength')) {
                <div>{{ 'PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS' | agrTranslate }}</div>
                }
              </div>
            </div>

            <!-- New Password -->
            <div id="my-profile-password" class="form-group">
              <label for="my-profile-password-input">{{ 'NEW_PASSWORD' | agrTranslate }}</label>
              <input type="password" id="my-profile-password-input" class="form-control" formControlName="password" autocomplete="off" />
              <div class="invalid-feedback">
                @if (password.getError('required')) {
                <div>{{ 'REQUIRED' | agrTranslate }}</div>
                } @if (password.getError('minlength')) {
                <div>{{ 'PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS' | agrTranslate }}</div>
                } @if (password.getError('maxlength')) {
                <div>{{ 'INPUT_IS_TOO_LONG' | agrTranslate }}</div>
                }
              </div>
            </div>

            <!-- Confirm Password -->
            <div id="my-profile-confirm-password" class="form-group">
              <label for="my-profile-confirm-password-input">{{ 'CONFIRM_PASSWORD' | agrTranslate }}</label>
              <input
                type="password"
                id="my-profile-confirm-password-input"
                class="form-control"
                [class.is-invalid]="submitted && (confirmPassword.errors || newPassword.getError('mismatch'))"
                formControlName="confirmPassword"
                autocomplete="off" />
              <div class="invalid-feedback">
                @if (submitted && newPassword.getError('mismatch')) {
                <div>{{ 'PASSWORDS_DO_NOT_MATCH' | agrTranslate }}</div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-link" (click)="onDismissModal()">{{ 'CANCEL' | agrTranslate }}</button>
      <button type="submit" class="btn btn-success">{{ 'SAVE' | agrTranslate }}</button>
    </div>
  </form>
</ng-template>
